// @ts-nocheck
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Card,
  IconButton,
  LinearProgress,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import Draggable from 'react-draggable';
import { SnackbarContent, useSnackbar } from 'notistack';
import PropTypes from 'prop-types';

import { AI_NOTIFICATION_TOAST_STATUS } from '../../const/CommonConst';
import { DEFAULT_ROUTE_CONFIG } from '../../const/defaultRoutesConfig';
import {
  getLabelPathFromLabelConfig,
  parseSafeString,
} from '../../utils/Utils';
import { clearActiveExaminationReviewToast } from '../ModelInventory/Dashboard/RegMapping/examinationReviewNotification';

const getToastPresentation = (status, palette, errorMessage) => {
  if (status === AI_NOTIFICATION_TOAST_STATUS.COMPLETED) {
    return {
      headerBg: palette?.AINotification?.completedHeaderBg,
      headerTitle: 'Examination review ready',
      iconBg: palette?.AINotification?.completedIconBg,
      iconColor: palette?.AINotification?.completedIconColor,
      subtitle: 'Report is ready. You can view it now.',
    };
  }

  if (status === AI_NOTIFICATION_TOAST_STATUS.FAILED) {
    return {
      headerBg: palette?.AINotification?.failedHeaderBg,
      headerTitle: 'Examination review failed',
      iconBg: palette?.AINotification?.failedIconBg,
      iconColor: palette?.AINotification?.failedIconColor,
      subtitle:
        errorMessage || 'Unable to complete the examination review request.',
    };
  }

  return {
    headerBg: palette?.AINotification?.inProgressHeaderBg,
    headerTitle: 'Examination review in progress',
    iconBg: palette?.AINotification?.inProgressIconBg,
    iconColor: palette?.AINotification?.inProgressIconColor,
    subtitle: 'Preparing your examination review report...',
  };
};

const getAssessmentExecutionStatus = (assessmentData = {}) => {
  const normalizedStatus = String(assessmentData?.status).trim().toLowerCase();

  if (normalizedStatus.includes('fail')) {
    return AI_NOTIFICATION_TOAST_STATUS.FAILED;
  }

  if (
    normalizedStatus.includes('pass') ||
    normalizedStatus.includes('complete')
  ) {
    return AI_NOTIFICATION_TOAST_STATUS.COMPLETED;
  }

  return AI_NOTIFICATION_TOAST_STATUS.IN_PROGRESS;
};

const updateForwardedRef = (forwardedRef, element) => {
  if (typeof forwardedRef === 'function') {
    forwardedRef(element);
    return;
  }

  if (forwardedRef) {
    const mutableRef = forwardedRef;
    mutableRef.current = element;
  }
};

function InnerCard({ data, onDismiss }) {
  const theme = useTheme();
  const requestStartedRef = useRef(false);
  const [toastData, setToastData] = useState(data);
  const status = getAssessmentExecutionStatus(toastData);
  const errorMessage =
    toastData?.error ||
    toastData?.message ||
    'Unable to complete the examination review request.';
  const displayName = toastData?.assessment_name;

  const toastPresentation = useMemo(() => {
    return getToastPresentation(status, theme?.palette, errorMessage);
  }, [errorMessage, status, theme?.palette]);

  useEffect(() => {
    if (requestStartedRef.current || !data?.runExaminationReview) {
      return;
    }

    requestStartedRef.current = true;

    data
      .runExaminationReview()
      .then((response) => {
        const assessmentData = response?.data || {};

        setToastData((prev) => ({
          ...prev,
          ...assessmentData,
          assessment_name: assessmentData?.assessment_name,
          status: assessmentData?.status,
        }));
      })
      .catch((error) => {
        setToastData((prev) => ({
          ...prev,
          status: AI_NOTIFICATION_TOAST_STATUS.FAILED,
          message:
            error?.response?.data?.message ||
            error?.response?.msg ||
            'Unable to start the examination review.',
        }));
      })
      .finally(() => {
        data?.onRequestCompleted?.();
      });
  }, [data]);

  const buildExaminationTabUrl = useCallback(() => {
    const homePath = getLabelPathFromLabelConfig(
      DEFAULT_ROUTE_CONFIG?.home?.keyPath || '',
      'path',
      DEFAULT_ROUTE_CONFIG?.home?.path
    );

    const examinationReviewUrl = new URL(
      `/${homePath}`,
      window.location.origin
    );

    examinationReviewUrl.searchParams.set('value', '6');

    return examinationReviewUrl;
  }, []);

  // If status of notification is complete/pass then redirect to view report so that user can see that particular report.
  const handleView = useCallback(() => {
    onDismiss();

    const examinationReviewUrl = buildExaminationTabUrl();
    if (toastData?.assessment_id) {
      examinationReviewUrl.searchParams.set(
        'regAssessmentId',
        String(toastData?.assessment_id)
      );
      examinationReviewUrl.searchParams.set('openRegResult', 'true');
    }

    const targetUrl = `${examinationReviewUrl.pathname}${examinationReviewUrl.search}`;
    window.history.pushState({}, '', targetUrl);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, [buildExaminationTabUrl, onDismiss, toastData?.assessment_id]);

  // If status of notification is fail then redirect user to Examination review tab
  const handleGoToPerformExamination = useCallback(() => {
    onDismiss();

    const examinationReviewUrl = buildExaminationTabUrl();
    const targetUrl = `${examinationReviewUrl.pathname}${examinationReviewUrl.search}`;
    window.history.pushState({}, '', targetUrl);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, [buildExaminationTabUrl, onDismiss]);

  const renderStatusIcon = useCallback(() => {
    if (status === AI_NOTIFICATION_TOAST_STATUS.COMPLETED) {
      return (
        <CheckCircleOutlineIcon
          fontSize="small"
          sx={{ color: toastPresentation?.iconColor }}
        />
      );
    }

    if (status === AI_NOTIFICATION_TOAST_STATUS.FAILED) {
      return (
        <ErrorOutlineOutlinedIcon
          fontSize="small"
          sx={{ color: toastPresentation?.iconColor }}
        />
      );
    }

    return (
      <DescriptionOutlinedIcon
        fontSize="small"
        sx={{ color: toastPresentation?.iconColor }}
      />
    );
  }, [status, toastPresentation?.iconColor]);

  return (
    <Card
      className="examination-review-notification"
      sx={{
        width: 320,
        boxShadow: 6,
        borderRadius: 1,
        overflow: 'hidden',
        pointerEvents: 'auto',
      }}
      aria-live="polite"
      aria-label={toastPresentation?.headerTitle}
    >
      <Box
        className="examination-review-notification-title"
        sx={{
          cursor: 'move',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 12px',
          backgroundColor: toastPresentation?.headerBg,
          color: theme?.palette?.AINotification?.headerTextColor,
          borderRadius: '4px 4px 0 0',
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <DragIndicatorIcon fontSize="small" />
          <Typography variant="body2" sx={{ color: 'inherit' }}>
            {toastPresentation?.headerTitle}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onDismiss}
          aria-label="Dismiss notification"
          sx={{
            color: theme?.palette?.AINotification?.headerTextColor,
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {status === AI_NOTIFICATION_TOAST_STATUS.IN_PROGRESS && (
        <LinearProgress
          sx={{
            height: 3,
            backgroundColor: theme?.palette?.AINotification?.progressTrackColor,
            '& .MuiLinearProgress-bar': {
              backgroundColor: theme?.palette?.AINotification?.progressBarColor,
            },
          }}
        />
      )}

      <Box
        sx={{
          padding: '12px 16px',
          backgroundColor: theme?.palette?.AINotification?.bodyBg,
          display: 'flex',
          gap: 1.5,
          alignItems: 'flex-start',
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            width: 36,
            height: 36,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: toastPresentation?.iconBg,
          }}
        >
          {renderStatusIcon()}
        </Box>

        <Box flex={1} minWidth={0}>
          <Typography
            variant="body2"
            fontWeight={600}
            noWrap
            title={displayName}
          >
            {displayName}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: theme?.palette?.AINotification?.subtitleColor,
              display: 'block',
              mt: 0.25,
            }}
          >
            {toastPresentation?.subtitle}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 1,
          padding: '0 16px 12px',
          backgroundColor: theme?.palette?.AINotification?.bodyBg,
        }}
      >
        {status === AI_NOTIFICATION_TOAST_STATUS.IN_PROGRESS && (
          <Button
            variant="outlined"
            size="small"
            sx={{ flex: 1, textTransform: 'none' }}
            aria-label="Preparing examination review"
            disabled
          >
            Preparing
          </Button>
        )}

        {status === AI_NOTIFICATION_TOAST_STATUS.COMPLETED && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleView}
            sx={{ flex: 1, textTransform: 'none', fontWeight: 600 }}
            aria-label={`View examination review for ${displayName}`}
          >
            View report
          </Button>
        )}

        {status === AI_NOTIFICATION_TOAST_STATUS.FAILED && (
          <Button
            variant="contained"
            size="small"
            onClick={handleGoToPerformExamination}
            sx={{ flex: 1, textTransform: 'none', fontWeight: 600 }}
            aria-label={`Go to perform examination for ${displayName}`}
          >
            Go to Perform Examination
          </Button>
        )}
      </Box>
    </Card>
  );
}

InnerCard.propTypes = {
  data: PropTypes.shape({
    toastId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    assessment_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    assessment_name: PropTypes.string,
    runExaminationReview: PropTypes.func,
    onRequestCompleted: PropTypes.func,
    status: PropTypes.oneOf([
      AI_NOTIFICATION_TOAST_STATUS.IN_PROGRESS,
      AI_NOTIFICATION_TOAST_STATUS.COMPLETED,
      AI_NOTIFICATION_TOAST_STATUS.FAILED,
    ]),
    error: PropTypes.string,
    message: PropTypes.string,
  }).isRequired,
  onDismiss: PropTypes.func.isRequired,
};

InnerCard.displayName = 'InnerCard';

const ExaminationReviewToast = forwardRef(
  function ExaminationReviewToast(props, ref) {
    const { id, message, runExaminationReview, onRequestCompleted } = props;
    const { closeSnackbar } = useSnackbar();
    const nodeRef = useRef(null);

    const parsedData = parseSafeString(message, {
      toastId: '',
      assessment_name: 'Examination review',
      status: AI_NOTIFICATION_TOAST_STATUS.FAILED,
    });
    const data = {
      ...parsedData,
      runExaminationReview,
      onRequestCompleted,
    };

    useEffect(() => {
      return () => {
        clearActiveExaminationReviewToast(data?.toastId, id);
      };
    }, [data?.toastId, id]);

    const handleDismiss = useCallback(() => {
      clearActiveExaminationReviewToast(data?.toastId, id);
      closeSnackbar(id);
    }, [closeSnackbar, data?.toastId, id]);

    const setRefs = useCallback(
      (element) => {
        nodeRef.current = element;
        updateForwardedRef(ref, element);
      },
      [ref]
    );

    return (
      <Draggable
        handle=".examination-review-notification-title"
        nodeRef={nodeRef}
      >
        <SnackbarContent ref={setRefs}>
          <InnerCard data={data} onDismiss={handleDismiss} />
        </SnackbarContent>
      </Draggable>
    );
  }
);

ExaminationReviewToast.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  message: PropTypes.string.isRequired,
  runExaminationReview: PropTypes.func,
  onRequestCompleted: PropTypes.func,
};

ExaminationReviewToast.displayName = 'ExaminationReviewToast';

export default ExaminationReviewToast;
